<template>
  <div>
    <div class="row items-center q-gutter-sm q-mb-md">
      <q-input
        v-model="searchFilter"
        dense
        outlined
        color="blue-brand"
        label="Search AGMs"
        clearable
        style="max-width: 200px"
      >
        <template #prepend>
          <q-icon name="search" />
        </template>
      </q-input>
      <q-btn
        flat
        dense
        :icon="sortAscending ? 'arrow_upward' : 'arrow_downward'"
        color="blue-brand"
        @click="sortAscending = !sortAscending"
      >
        <q-tooltip>{{ sortAscending ? 'Ascending' : 'Descending' }}</q-tooltip>
      </q-btn>
    </div>
    <div class="row q-gutter-md">
      <div class="col">
        <div class="row items-center justify-between q-mb-sm">
          <div class="text-bold">All in Group ({{ availableAgms.length }})</div>
          <div class="row items-center q-gutter-xs">
            <q-btn
              flat
              dense
              no-caps
              :color="isTrimmed ? 'orange-8' : 'blue-grey-6'"
              :label="isTrimmed ? 'Reset' : 'Trim'"
              :disable="!isTrimmed && !hasTrimableAgms"
              @click="onTrim"
            >
              <q-tooltip v-if="!isTrimmed"
                >This will remove the AGMs that are currently involved in active
                pig runs</q-tooltip
              >
            </q-btn>
            <q-btn
              flat
              dense
              no-caps
              color="blue-brand"
              :label="allAvailableSelected ? 'Deselect All' : 'Select All'"
              @click="onToggleSelectAllAvailable"
            />
          </div>
        </div>
        <div class="row items-center q-gutter-xs q-mb-sm">
          <q-input
            v-model="availableRangeFrom"
            dense
            outlined
            type="number"
            label="From"
            style="max-width: 80px"
            color="blue-brand"
          />
          <q-input
            v-model="availableRangeTo"
            dense
            outlined
            type="number"
            label="To"
            style="max-width: 80px"
            color="blue-brand"
          />
          <q-btn
            flat
            dense
            no-caps
            color="blue-brand"
            :label="
              availableRangeCount > 0
                ? `Select Range (${availableRangeCount})`
                : 'Select Range'
            "
            :disable="!availableRangeFrom || !availableRangeTo"
            @click="onSelectAvailableRange"
          />
        </div>
        <q-list
          ref="availableList"
          bordered
          separator
          class="rounded-borders"
          style="min-width: 250px; height: 300px; overflow-y: auto"
        >
          <q-item
            v-for="agm in filteredAvailableAgms"
            :key="'avail-' + agm.value"
            clickable
            :active="availableSelection.some((s) => s.value === agm.value)"
            active-class="bg-blue-1 text-blue-brand"
            @click="onToggleAvailable(agm)"
          >
            <q-item-section>{{ agm.label }}</q-item-section>
          </q-item>
        </q-list>
      </div>
      <div class="col-auto column justify-center q-gutter-sm">
        <q-btn
          class="btn-brand"
          label=">>"
          dense
          style="min-width: 60px"
          @click="onMoveRight"
        />
        <q-btn
          class="btn-brand"
          label="<<"
          dense
          style="min-width: 60px"
          @click="onMoveLeft"
        />
      </div>
      <div class="col">
        <div class="row items-center justify-between q-mb-sm">
          <div class="text-bold">Selected ({{ selectedAgms.length }})</div>
          <q-btn
            flat
            dense
            no-caps
            color="blue-brand"
            :label="allSelectedSelected ? 'Deselect All' : 'Select All'"
            @click="onToggleSelectAllSelected"
          />
        </div>
        <div class="row items-center q-gutter-xs q-mb-sm">
          <q-input
            v-model="selectedRangeFrom"
            dense
            outlined
            type="number"
            label="From"
            style="max-width: 80px"
            color="blue-brand"
          />
          <q-input
            v-model="selectedRangeTo"
            dense
            outlined
            type="number"
            label="To"
            style="max-width: 80px"
            color="blue-brand"
          />
          <q-btn
            flat
            dense
            no-caps
            color="blue-brand"
            :label="
              selectedRangeCount > 0
                ? `Select Range (${selectedRangeCount})`
                : 'Select Range'
            "
            :disable="!selectedRangeFrom || !selectedRangeTo"
            @click="onSelectSelectedRange"
          />
        </div>
        <q-list
          ref="selectedList"
          bordered
          separator
          class="rounded-borders"
          style="min-width: 250px; height: 300px; overflow-y: auto"
        >
          <q-item
            v-for="agm in sortedSelectedAgms"
            :key="'sel-' + agm.value"
            clickable
            :active="selectedSelection.some((s) => s.value === agm.value)"
            active-class="bg-blue-1 text-blue-brand"
            @click="onToggleSelected(agm)"
          >
            <q-item-section>{{ agm.label }}</q-item-section>
          </q-item>
        </q-list>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent, PropType } from 'vue';

interface AgmItem {
  label: string;
  value: number;
}

export default defineComponent({
  name: 'AgmPicker',
  props: {
    availableAgms: {
      type: Array as PropType<AgmItem[]>,
      required: true,
    },
    selectedAgms: {
      type: Array as PropType<AgmItem[]>,
      required: true,
    },
    activeRunAgmIds: {
      type: Array as PropType<number[]>,
      default: () => [],
    },
  },
  emits: ['update:availableAgms', 'update:selectedAgms'],
  data() {
    return {
      availableSelection: [] as AgmItem[],
      selectedSelection: [] as AgmItem[],
      searchFilter: '' as string,
      sortAscending: true,
      isTrimmed: false,
      trimmedAgms: [] as AgmItem[],
      availableRangeFrom: '' as string,
      availableRangeTo: '' as string,
      selectedRangeFrom: '' as string,
      selectedRangeTo: '' as string,
    };
  },
  computed: {
    filteredAvailableAgms(): AgmItem[] {
      let list = [...this.availableAgms];
      if (this.searchFilter) {
        const q = this.searchFilter.toLowerCase();
        list = list.filter((a) => a.label.toLowerCase().includes(q));
      }
      list.sort((a, b) =>
        this.sortAscending ? a.value - b.value : b.value - a.value
      );
      return list;
    },
    sortedSelectedAgms(): AgmItem[] {
      return [...this.selectedAgms].sort((a, b) =>
        this.sortAscending ? a.value - b.value : b.value - a.value
      );
    },
    allAvailableSelected(): boolean {
      return (
        this.filteredAvailableAgms.length > 0 &&
        this.filteredAvailableAgms.every((agm) =>
          this.availableSelection.some((s) => s.value === agm.value)
        )
      );
    },
    allSelectedSelected(): boolean {
      return (
        this.sortedSelectedAgms.length > 0 &&
        this.sortedSelectedAgms.every((agm) =>
          this.selectedSelection.some((s) => s.value === agm.value)
        )
      );
    },
    hasTrimableAgms(): boolean {
      if (this.isTrimmed) return false;
      return this.availableAgms.some((a) =>
        this.activeRunAgmIds.includes(a.value)
      );
    },
    availableRangeCount(): number {
      const from = parseInt(this.availableRangeFrom, 10);
      const to = parseInt(this.availableRangeTo, 10);
      if (isNaN(from) || isNaN(to) || from > to) return 0;
      return this.filteredAvailableAgms.filter(
        (a) => a.value >= from && a.value <= to
      ).length;
    },
    selectedRangeCount(): number {
      const from = parseInt(this.selectedRangeFrom, 10);
      const to = parseInt(this.selectedRangeTo, 10);
      if (isNaN(from) || isNaN(to) || from > to) return 0;
      return this.sortedSelectedAgms.filter(
        (a) => a.value >= from && a.value <= to
      ).length;
    },
  },
  methods: {
    onToggleAvailable(agm: AgmItem) {
      const idx = this.availableSelection.findIndex(
        (s) => s.value === agm.value
      );
      if (idx >= 0) {
        this.availableSelection.splice(idx, 1);
      } else {
        this.availableSelection.push(agm);
      }
    },
    onToggleSelected(agm: AgmItem) {
      const idx = this.selectedSelection.findIndex(
        (s) => s.value === agm.value
      );
      if (idx >= 0) {
        this.selectedSelection.splice(idx, 1);
      } else {
        this.selectedSelection.push(agm);
      }
    },
    onToggleSelectAllAvailable() {
      if (this.allAvailableSelected) {
        this.availableSelection = [];
      } else {
        this.availableSelection = [...this.filteredAvailableAgms];
      }
    },
    onToggleSelectAllSelected() {
      if (this.allSelectedSelected) {
        this.selectedSelection = [];
      } else {
        this.selectedSelection = [...this.sortedSelectedAgms];
      }
    },
    onMoveRight() {
      const available = [...this.availableAgms];
      const selected = [...this.selectedAgms];
      for (const agm of this.availableSelection) {
        const idx = available.findIndex((a) => a.value === agm.value);
        if (idx !== -1) available.splice(idx, 1);
        selected.push(agm);
      }
      this.availableSelection = [];
      this.$emit('update:availableAgms', available);
      this.$emit('update:selectedAgms', selected);
    },
    onMoveLeft() {
      const available = [...this.availableAgms];
      const selected = [...this.selectedAgms];
      for (const agm of this.selectedSelection) {
        const idx = selected.findIndex((a) => a.value === agm.value);
        if (idx !== -1) selected.splice(idx, 1);
        available.push(agm);
      }
      available.sort((a, b) => a.value - b.value);
      this.selectedSelection = [];
      this.$emit('update:availableAgms', available);
      this.$emit('update:selectedAgms', selected);
    },
    onTrim() {
      if (this.isTrimmed) {
        // Restore trimmed AGMs back to available
        const restored = [...this.availableAgms, ...this.trimmedAgms].sort(
          (a, b) => a.value - b.value
        );
        this.trimmedAgms = [];
        this.isTrimmed = false;
        this.$emit('update:availableAgms', restored);
      } else {
        const toTrim: AgmItem[] = [];
        const remaining: AgmItem[] = [];
        for (const agm of this.availableAgms) {
          if (this.activeRunAgmIds.includes(agm.value)) {
            toTrim.push(agm);
          } else {
            remaining.push(agm);
          }
        }
        this.trimmedAgms = toTrim;
        this.isTrimmed = true;
        // Clear any highlight selections for removed items
        this.availableSelection = this.availableSelection.filter((s) =>
          remaining.some((a) => a.value === s.value)
        );
        this.$emit('update:availableAgms', remaining);
      }
    },
    onSelectAvailableRange() {
      const from = parseInt(this.availableRangeFrom, 10);
      const to = parseInt(this.availableRangeTo, 10);
      if (isNaN(from) || isNaN(to)) return;
      const inRange = this.filteredAvailableAgms.filter(
        (a) => a.value >= from && a.value <= to
      );
      const existing = new Set(this.availableSelection.map((s) => s.value));
      for (const agm of inRange) {
        if (!existing.has(agm.value)) {
          this.availableSelection.push(agm);
        }
      }
      if (inRange.length > 0) {
        this.$nextTick(() => {
          const list = (this.$refs.availableList as any)?.$el as
            | HTMLElement
            | undefined;
          const first = list?.querySelector('.q-item--active');
          if (first) first.scrollIntoView({ block: 'nearest' });
        });
      }
    },
    onSelectSelectedRange() {
      const from = parseInt(this.selectedRangeFrom, 10);
      const to = parseInt(this.selectedRangeTo, 10);
      if (isNaN(from) || isNaN(to)) return;
      const inRange = this.sortedSelectedAgms.filter(
        (a) => a.value >= from && a.value <= to
      );
      const existing = new Set(this.selectedSelection.map((s) => s.value));
      for (const agm of inRange) {
        if (!existing.has(agm.value)) {
          this.selectedSelection.push(agm);
        }
      }
      if (inRange.length > 0) {
        this.$nextTick(() => {
          const list = (this.$refs.selectedList as any)?.$el as
            | HTMLElement
            | undefined;
          const first = list?.querySelector('.q-item--active');
          if (first) first.scrollIntoView({ block: 'nearest' });
        });
      }
    },
  },
});
</script>
